<template>
  <q-table
    :columns="cols"
    :rows="rows"
    class="external-dns-table"
    :class="{ empty: rows.length === 0 }"
    ref="itemsTable"
    row-key="id"
    flat
    v-model:pagination="pagination"
    :loading="loading"
    binary-state-sort
    :rows-per-page-options="perPageOptions"
    @request="load"
    :expanded="expanded"
  >
    <template v-slot:no-data>
      <div class="flex full-width column items-center q-py-lg">
        <q-icon name="mdi-cloud-search" class="q-mb-sm" size="40px" color="primary"></q-icon>
        <span class="block" style="font-size: 14px">No items were found.</span>
      </div>
    </template>

    <template v-slot:body="props">
      <q-tr :props="props">
        <q-td v-for="col in props.cols" :key="col.name" :props="props" :align="col.align">
          <div v-if="col.name === 'status'">
            <StatusCell v-if="!props.row.deleted_at" :status="props.row.status" />
            <q-icon v-else name="close" color="grey" size="15px"></q-icon>
          </div>
          <div v-else-if="col.name === 'dns'">
            <q-icon v-if="props.row.dns" name="check" size="15px"></q-icon>
            <q-icon v-else name="remove" size="15px"></q-icon>
          </div>
          <div v-else-if="col.name === 'token_url'">
            <a v-if="props.row.token_url" :href="props.row.token_url" target="_blank">
              <q-icon name="sym_r_link" size="18px"></q-icon>
            </a>
            <span v-else>-</span>
          </div>
          <div v-else-if="col.name === 'changeLink'">
            <a v-if="props.row.last_change_link" target="_blank" :href="props.row.last_change_link">{{
              props.row.last_change_number || 'Change Request'
            }}</a>
            <span v-else>-</span>
          </div>
          <div v-else-if="col.name === 'digitalService'">
            <router-link :to="`/my-portfolio/${props.row.registration?.digitalService?.apmId}`">{{
              props.row.registration?.digitalService?.apmId
            }}</router-link>
          </div>
          <div v-else-if="col.name === 'actions'">
            <q-btn
              v-if="mode === 'list'"
              flat
              round
              size="8px"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :ripple="false"
              icon="engp:engp-ico-registry_outline"
              @click.stop="goToRegistration($event, props.row)"
            >
              <q-tooltip>Go to registration</q-tooltip>
            </q-btn>
            <WorkflowsBtn
              @showWorkflow="showWorkflow($event, props.row)"
              :loading="loading"
              :relatedWorkflowStatuses="relatedWorkflowStatuses.find((i) => i.id === props.row.id)?.statuses || []"
            />
            <q-btn
              v-if="mode === 'edit' && !props.row.deleted_at"
              flat
              round
              size="12px"
              icon="sym_r_edit"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :loading="loading"
              :disabled="loading || disableEditIds.find((item) => item.id === props.row.id)"
              @click="$emit('toggleExpanded', props.row)"
            >
              <q-tooltip v-if="!loading" max-width="480px" style="white-space: pre-line">{{
                !!disableEditIds.find((item) => item.id === props.row.id)
                  ? disableEditIds.find((item) => item.id === props.row.id)?.message
                  : 'Edit license'
              }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="mode === 'edit' && !props.row.deleted_at"
              flat
              round
              size="13px"
              icon="sym_o_compare_arrows"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
              :loading="loading"
              :disabled="loading || disableReassignIds.find((item) => item.id === props.row.id)"
              @click="$emit('reassign', props.row)"
            >
              <q-tooltip v-if="!loading" max-width="480px" style="white-space: pre-line">{{
                !!disableReassignIds.find((item) => item.id === props.row.id)
                  ? disableReassignIds.find((item) => item.id === props.row.id)?.message
                  : 'Reassign zone'
              }}</q-tooltip>
            </q-btn>
            <q-btn
              v-if="mode === 'edit' && !props.row.deleted_at"
              flat
              round
              size="12px"
              @click="$emit('confirmDeleteZone', props.row)"
              :loading="loading"
              :disabled="loading || disableDeleteIds.find((item) => item.id === props.row.id)"
              icon="sym_r_delete"
              style="width: 30px; height: 40px"
              class="btn--no-hover"
            >
              <q-tooltip max-width="480px" style="white-space: pre-line" v-if="!loading">{{
                disableDeleteIds.find((item) => item.id === props.row.id)
                  ? disableDeleteIds.find((item) => item.id === props.row.id).message
                  : 'Delete'
              }}</q-tooltip>
            </q-btn>
            <CopyBtn :copyData="props.row.id" notification="Zone UUID was copied" />
          </div>
          <div v-else>{{ col.value }}</div>
        </q-td>
      </q-tr>
      <q-tr v-if="expanded?.length ? props.row.id === expanded[0] : false" :props="props"
        ><slot name="expand"></slot
      ></q-tr>
    </template>
  </q-table>
</template>

<script>
import dayjs from 'dayjs';
import { supportCntrlClick } from '../helpers';
import { getZones } from '@/pages/allItems/api/api';
import { getRelatedWorkflowsStatuses } from '@/pages/registrations/api/api';
import { validateWorkFlow, subValWorkflow } from '@/pages/registrations/api/api';
import StatusCell from '@/pages/registrations/components/registration-network/components/StatusCell.vue';
import CopyBtn from './CopyBtn.vue';
import WorkflowsBtn from './WorkflowsBtn.vue';

export default {
  name: 'ZonesTable',
  components: {
    CopyBtn,
    StatusCell,
    WorkflowsBtn,
  },
  props: {
    rowsPerPageOptions: {
      type: Array,
      default: () => [5, 7, 10, 15, 20],
    },
    filters: {
      type: Object,
    },
    registrationId: {
      type: String,
    },
    registrationData: {
      type: Object,
    },
    mode: {
      type: String,
    },
    expanded: {
      type: Array,
    },
  },
  data() {
    return {
      perPageOptions: this.rowsPerPageOptions,
      relatedWorkflowStatuses: [],
      disableDeleteIds: [],
      disableReassignIds: [],
      disableEditIds: [],
      rows: [],
      loading: false,
      pagination: {
        sortBy: 'created_at',
        descending: true,
        page: 1,
        rowsPerPage: this.mode === 'list' ? 20 : 5,
      },
      baseCols: [
        {
          label: 'Status',
          name: 'status',
          field: 'status',
          align: 'left',
        },
        {
          label: 'Name',
          name: 'zone_name',
          field: 'zone_name',
          align: 'left',
        },
        {
          label: 'License',
          name: 'license',
          field: 'license',
          align: 'left',
        },
        {
          label: 'DNS',
          name: 'dns',
          field: 'dns',
          align: 'center',
        },
        {
          label: 'Token URL',
          name: 'token_url',
          field: 'token_url',
          align: 'center',
        },
        {
          label: 'Change Request',
          name: 'changeLink',
          field: 'changeLink',
          align: 'center',
        },
        {
          label: 'Digital Service',
          name: 'digitalService',
          field: 'digitalService',
          align: 'left',
        },
        {
          label: 'Created at',
          name: 'created_at',
          field: 'created_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: 'Updated at',
          name: 'updated_at',
          field: 'updated_at',
          align: 'left',
          format: (val) => dayjs(val).format('DD.MM.YYYY HH:mm'),
          sortable: true,
        },
        {
          label: '',
          name: 'actions',
          field: 'actions',
          align: 'right',
        },
      ],
    };
  },
  computed: {
    cols() {
      if (this.mode !== 'list') {
        return this.baseCols.filter((col) => col.name !== 'digitalService');
      }
      return this.baseCols;
    },
  },
  methods: {
    goToRegistration(event, record) {
      supportCntrlClick(
        event,
        `/my-portfolio/${record.registration?.digitalService?.apmId}/registration/${record.registration.id}?zoneId=${record.id}`,
      );
    },
    showWorkflow(event, record) {
      supportCntrlClick(event, `/workflows?itemId=${record.id}`);
    },
    async load(props) {
      this.loading = true;
      try {
        this.relatedWorkflowStatuses = [];
        this.disableDeleteIds = [];
        this.disableReassignIds = [];
        this.disableEditIds = [];
        const limit = props.pagination.rowsPerPage;
        const { sortBy, descending } = props.pagination;
        const skip = props.pagination.page === 1 ? 0 : (props.pagination.page - 1) * props.pagination.rowsPerPage;
        const {
          id,
          registrationId,
          digitalService,
          infraService,
          changeNumber,
          status,
          name,
          license,
          dns_enabled,
          dateFrom,
          dateTo,
        } = this.filters;
        const { count, data } = await getZones(
          limit,
          skip,
          sortBy,
          descending,
          id,
          registrationId,
          digitalService,
          infraService,
          changeNumber,
          status,
          name,
          license,
          dns_enabled,
          dateFrom,
          dateTo,
        );
        this.pagination.rowsNumber = count;
        this.rows = data;
        this.pagination.page = props.pagination.page;
        this.pagination.sortBy = sortBy;
        this.pagination.descending = descending;
        this.pagination.rowsPerPage = props.pagination.rowsPerPage;
        if (this.mode === 'list') {
          this.$router.replace({ query: this.buildQueryFromFilters() });
        }
        if (this.mode === 'edit' && this.filters.status !== 'removed') {
          const deletePrevalPromises = this.rows.map(async (zone) => {
            const data = {
              workflowId: 'engprt.delete-cloudflare-zone',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id: zone.id,
              },
            };
            return validateWorkFlow(data).catch((res) => {
              this.disableDeleteIds.push({ id: zone.id, message: res.message });
            });
          });
          const movePrevalPromises = this.rows.map(async (zone) => {
            const { id } = zone;
            const data = {
              workflowId: 'engprt.move-cloudflare-zone',
              subSchemaId: 'move-button',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id,
              },
            };
            return subValWorkflow(data).catch((res) => {
              this.disableReassignIds.push({ id: data.inputVariables.id, message: res.message });
            });
          });
          const editPrevalPromises = this.rows.map(async (zone) => {
            const data = {
              workflowId: 'engprt.update-cloudflare-zone',
              subSchemaId: 'edit-button',
              inputVariables: {
                digitalServiceApmId: this.registrationData.digitalServiceApm,
                id: zone.id,
              },
            };
            return subValWorkflow(data).catch((res) => {
              this.disableEditIds.push({ id: zone.id, message: res.message });
            });
          });
          await Promise.all([...deletePrevalPromises, ...movePrevalPromises, ...editPrevalPromises]);
        }
        const relatedWorkflowsPrmises = this.rows.map((record) => {
          return getRelatedWorkflowsStatuses(record.id).then((response) => {
            this.relatedWorkflowStatuses.push({ id: record.id, statuses: response });
          });
        });
        await Promise.all([...relatedWorkflowsPrmises]);
      } catch (error) {
        this.$notify('negative', 'Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    buildQueryFromFilters() {
      const query = {};
      for (const key in this.filters) {
        if (this.filters[key]) {
          query[key] = this.filters[key];
        }
      }
      query[this.pagination.sortBy] = this.pagination.descending ? 'DESC' : 'ASC';
      return query;
    },
    reload() {
      this.$refs.itemsTable.requestServerInteraction();
    },
  },
  mounted() {
    if (this.mode === 'list') {
      const query = this.$route.query;
      const sortFields = ['created_at', 'updated_at'];

      for (const field of sortFields) {
        if (query?.[field]) {
          this.pagination.sortBy = field;
          this.pagination.descending = query[field] !== 'ASC';
        }
      }
    }
  },
};
</script>
